import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { useAui, useAuiState } from "@assistant-ui/react";
import { ActiveChatTitle, Chat, ChatComposer } from "@/elements";
import {
  ChevronLeft,
  Home,
  Loader2,
  MessageCircle,
  Minus,
  Pin,
  Plus,
  SquarePen,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type { ChatOverview } from "@gram/client/models/components/chatoverview.js";
import {
  Pinned,
  SortBy,
  SortOrder,
} from "@gram/client/models/operations/listchats";
import { useChatSetPinnedMutation } from "@gram/client/react-query/chatSetPinned.js";
import {
  invalidateAllListChats,
  useListChats,
} from "@gram/client/react-query/listChats.js";
import { useMembers } from "@gram/client/react-query/members.js";
import { useSession } from "@/contexts/Auth";
import { resolveChatOwner } from "@/lib/chat-owner";
import {
  BRAND_MESH_SURFACE_CLASS,
  BrandMeshLayers,
} from "@/components/brand-mesh";
import { getIdentityTint, useIsDarkTheme } from "@/components/gradient-colors";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import {
  useHideInsightsDock,
  useInsightsState,
} from "@/components/insights-context";
import { useServerAssistantTransport } from "@/hooks/useServerAssistantTransport";
import { useSlugs } from "@/contexts/Sdk";
import {
  CHAT_LANDING_SUGGESTIONS,
  INSIGHTS_SUGGESTION_ICONS,
  SLASH_COMMANDS,
  type InsightsSuggestion,
} from "@/lib/insights-suggestions";
import { useChatLaunch } from "@/lib/chat-launch";
import { cn } from "@/lib/utils";
import { ReleaseStageBadge } from "@/components/release-stage-badge";
import { useRecentLabelOverride } from "@/components/command-palette/recentlyVisited";
import { FALLBACK_TITLE } from "@/elements/components/activeChatTitle.helpers";
import { useRoutes } from "@/routes";
import { RequireScope } from "@/components/require-scope";
import { useProject } from "@/contexts/Auth";

// Shared square icon button used by the page chrome (back affordances).
// bg-card so the button stays a solid chip when it sits on the brand mesh;
// hover darkens the border (and text) rather than filling the chip grey.
const ICON_BUTTON_CLASS =
  "border-border bg-card text-muted-foreground hover:border-foreground hover:text-foreground flex items-center gap-1 border px-2.5 py-1.5 transition-colors";

/** Layout route for `/chat`; renders the index (home) or a conversation. */
export function ChatRoot(): ReactElement {
  const project = useProject();
  // The page IS the chat, so hide the floating dock across the /chat subtree.
  useHideInsightsDock();
  return (
    <RequireScope scope="assistant:read" projectId={project.id} level="page">
      <Outlet />
    </RequireScope>
  );
}

/**
 * `/chat` landing — a full-page "Ask anything" entry point (a second way into
 * the Project Assistant alongside the docked composer).
 */
export function ChatHome(): ReactElement {
  const routes = useRoutes();
  return (
    // Same brand-mesh surface as the project home assistant card, scaled to
    // the page: neutral theme-following gradient with the rainbow edge and
    // grain. Scrolling lives on an inner wrapper so the mesh (and the back
    // affordance) stay pinned to the viewport instead of scrolling away with
    // the content.
    <div
      className={cn(
        BRAND_MESH_SURFACE_CLASS,
        "flex h-[calc(100vh-var(--banner-offset,0px))] flex-col",
      )}
    >
      <BrandMeshLayers />
      {/* Header row on the same --header-height grid as Page.Header, but with
          no rule below it — the mesh surface should read unbroken here. */}
      <header className="relative z-10 flex h-(--header-height) shrink-0 items-center px-8">
        <Link
          to={routes.home.href()}
          aria-label="Back to home"
          className={ICON_BUTTON_CLASS}
        >
          <ChevronLeft className="size-4" />
          <Home className="size-4" />
        </Link>
      </header>
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col px-6 pt-[clamp(10rem,26vh,16rem)] pb-16">
          <ChatLanding autoFocusInput />
        </div>
      </div>
    </div>
  );
}

// Example questions cycled through the composer placeholder — deliberately
// different from the suggestion chips below, to hint at the assistant's range.
const ASK_PLACEHOLDERS = [
  "Summarize this week's activity",
  "What should I look into today?",
  "How did usage change vs last week?",
  "Which agents are the most active?",
  "Show me recent failed tool calls",
  "What's my busiest MCP server?",
  "Draft a weekly usage recap",
];

const PLACEHOLDER_HOLD_MS = 4800;
const PLACEHOLDER_FADE_MS = 300;

/** Rotates the composer placeholder through ASK_PLACEHOLDERS, holding each then
 *  crossfading to the next. `visible` drives the fade; honours
 *  prefers-reduced-motion by holding on the first. */
function useCyclingPlaceholder(): { text: string; visible: boolean } {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let fadeId: ReturnType<typeof setTimeout>;
    const holdId = setInterval(() => {
      setVisible(false); // fade current out
      fadeId = setTimeout(() => {
        setIndex((n) => (n + 1) % ASK_PLACEHOLDERS.length);
        setVisible(true); // fade next in
      }, PLACEHOLDER_FADE_MS);
    }, PLACEHOLDER_HOLD_MS);
    return () => {
      clearInterval(holdId);
      clearTimeout(fadeId);
    };
  }, []);
  return { text: ASK_PLACEHOLDERS[index] ?? "Ask anything", visible };
}

/**
 * The "Ask anything" widget — greeting, composer, recents, recipes. Used by
 * the `/chat` landing and embedded on the project home page. Submitting opens
 * a fresh conversation on the shared runtime and navigates to the full-page
 * chat; the server mints the chat id on the first send.
 *
 * `compact` drops the pinned/recents history so the widget can sit inside the
 * project home page without competing with the dashboard below it.
 */
export function ChatLanding({
  autoFocusInput = false,
  compact = false,
}: {
  autoFocusInput?: boolean;
  compact?: boolean;
}): ReactElement {
  const { user } = useSession();
  const navigate = useNavigate();
  const routes = useRoutes();
  const { sendPrompt, assistantNeedsAdmin, assistantReady } =
    useInsightsState();
  const [value, setValue] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [activeCommand, setActiveCommand] = useState(0);
  const { text: placeholder, visible: placeholderVisible } =
    useCyclingPlaceholder();

  // Slash menu: typing "/" opens a filtered palette of starter prompts. The
  // query is everything after the leading slash; matching is substring across
  // the command title, label, and full prompt.
  const slashQuery = value.startsWith("/") ? value.slice(1) : null;
  const slashCommands = useMemo(() => {
    if (slashQuery === null) return [];
    const q = slashQuery.trim().toLowerCase();
    if (!q) return SLASH_COMMANDS;
    return SLASH_COMMANDS.filter(
      (command) =>
        command.title.toLowerCase().includes(q) ||
        command.label.toLowerCase().includes(q) ||
        command.prompt.toLowerCase().includes(q),
    );
  }, [slashQuery]);
  const slashOpen = inputFocused && slashCommands.length > 0;

  // Reset the highlighted row whenever the query (and therefore the filtered
  // list) changes, so the selection never points past the end.
  useEffect(() => {
    setActiveCommand(0);
  }, [slashQuery]);

  const firstName = user.displayName?.trim().split(/\s+/)[0];
  const greeting = firstName
    ? `Hi ${firstName}, ask your Project Assistant about your AI usage`
    : "Ask your Project Assistant about your AI usage";
  // Split off the last word so the BETA badge can be glued to it with
  // whitespace-nowrap — the badge then never wraps onto a line of its own.
  const greetingWords = greeting.split(" ");
  const greetingLead = greetingWords.slice(0, -1).join(" ");
  const greetingLast = greetingWords[greetingWords.length - 1];

  const startChat = (prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    // Start the conversation on the shared runtime, then drop into the
    // full-page view — the queued prompt fires once the chat route mounts the
    // runtime. The server mints the chat id on the first send.
    sendPrompt(trimmed);
    void navigate(routes.chat.conversation.href("new"));
  };

  const submit = () => {
    if (value.startsWith("/")) {
      // Slash mode: Enter runs the highlighted command — never send the raw
      // "/…" text as a prompt.
      const command = slashCommands[activeCommand];
      if (command) startChat(command.prompt);
      return;
    }
    startChat(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && value.startsWith("/")) {
      e.preventDefault();
      setValue("");
      return;
    }
    if (!slashOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveCommand((i) => (i + 1) % slashCommands.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveCommand(
        (i) => (i - 1 + slashCommands.length) % slashCommands.length,
      );
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h1
          className={cn(
            "font-display font-thin",
            compact ? "text-2xl" : "text-4xl",
            "text-foreground",
          )}
        >
          {greetingLead}{" "}
          <span className="whitespace-nowrap">
            {greetingLast}
            <ReleaseStageBadge stage="beta" className="ml-3 align-middle" />
          </span>
        </h1>
        {assistantNeedsAdmin ? (
          <p className="border-border bg-card text-muted-foreground border px-4 py-3 text-sm">
            Ask an admin to enable the Project Assistant for this project.
          </p>
        ) : assistantReady ? (
          // The real AUI composer, bound to the shared runtime: sending happens
          // in the runtime itself, so this entry point has dictation,
          // attachments, tool mentions and skill context like every other one.
          // Navigation to the conversation view is driven by the first message
          // landing in the thread, not by this component sending.
          // `gram-chat-landing` is read from inside the Elements shadow root
          // via :host-context, so it must sit on an ancestor of the host — not
          // on anything ChatComposer renders inside it.
          <div
            className="gram-chat-landing border-border bg-card border"
            // Cycling example prompts ride in as custom properties: they
            // inherit into the Elements shadow root, so the placeholder can
            // crossfade without re-rendering the composer (see
            // CHAT_LANDING_COMPOSER_CSS).
            style={
              {
                "--gram-composer-placeholder": JSON.stringify(placeholder),
                "--gram-composer-placeholder-opacity": placeholderVisible
                  ? 1
                  : 0,
              } as CSSProperties
            }
          >
            <ChatComposer />
            <StartFreshThread />
            <SeedComposerDraft value={value} onSeeded={() => setValue("")} />
            <NavigateOnFirstMessage />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="border-border bg-card focus-within:border-foreground relative border px-4 py-3 transition-colors"
          >
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              aria-label="Ask your Project Assistant about your AI usage"
              role="combobox"
              aria-expanded={slashOpen}
              aria-controls="ask-slash-menu"
              aria-activedescendant={
                slashOpen ? `ask-slash-${activeCommand}` : undefined
              }
              autoFocus={autoFocusInput}
              className="w-full bg-transparent text-base outline-none"
            />
            {/* Overlay placeholder so it can crossfade (native ::placeholder
              can't transition between values). Shown only while empty; the
              kbd hint advertises the slash menu. */}
            {value === "" && (
              <>
                <span
                  aria-hidden="true"
                  className="text-muted-foreground pointer-events-none absolute top-1/2 right-36 left-4 -translate-y-1/2 truncate text-base transition-opacity duration-300"
                  style={{ opacity: placeholderVisible ? 1 : 0 }}
                >
                  {placeholder}
                </span>
                <div className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 flex items-center gap-1.5 text-xs">
                  <kbd className="border-border border px-1.5 py-0.5 font-mono">
                    /
                  </kbd>
                  for suggestions
                </div>
              </>
            )}
            {slashOpen && (
              <SlashCommandMenu
                commands={slashCommands}
                activeIndex={activeCommand}
                onHover={setActiveCommand}
                onSelect={(command) => startChat(command.prompt)}
              />
            )}
          </form>
        )}
      </div>

      {compact ? (
        // Side-by-side columns so the card stays short: starters on the left,
        // a peek at recent threads on the right.
        <div className="flex flex-col gap-6 md:flex-row md:gap-8">
          {!assistantNeedsAdmin && (
            <div className="flex min-w-0 flex-1 flex-col">
              <ChatHomeSuggestions compact />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <ChatHomeCompactRecents />
          </div>
        </div>
      ) : (
        <>
          <ChatHomePinned />
          <ChatHomeRecents />
          {!assistantNeedsAdmin && <ChatHomeSuggestions />}
        </>
      )}
    </div>
  );
}

/**
 * Carries a draft typed into the fallback input over to the shared composer.
 *
 * The fallback renders only until the assistant runtime is ready; the two
 * controls keep their drafts in different places, so without this a message
 * typed during that window vanishes when the composer swaps in. Runs after
 * StartFreshThread so the text lands on the new thread, not the old one.
 */
function SeedComposerDraft({
  value,
  onSeeded,
}: {
  value: string;
  onSeeded: () => void;
}): null {
  const aui = useAui();
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !value.trim()) return;
    seededRef.current = true;
    aui.composer().setText(value);
    onSeeded();
  }, [aui, value, onSeeded]);
  return null;
}

/**
 * The landing widgets always start a NEW conversation: they share one runtime
 * with the dock, and the dock is the only surface that deliberately continues
 * an existing chat. Without this, opening /chat (or project home) after a dock
 * conversation would drop the next message into that thread.
 */
function StartFreshThread(): null {
  const aui = useAui();
  // Switch unconditionally, exactly once, as soon as the thread list has
  // loaded. Reacting to "the thread has messages" instead would fire on the
  // user's OWN send — switching the just-sent message out from under them and
  // leaving an empty /chat/new. Thread ids are minted lazily
  // (deferThreadIdMinting), so a visit that sends nothing costs no server chat.
  const listLoading = useAuiState(({ threads }) => threads.isLoading);
  const switchedRef = useRef(false);
  useEffect(() => {
    if (switchedRef.current || listLoading) return;
    switchedRef.current = true;
    aui.threads().switchToNewThread();
  }, [aui, listLoading]);
  return null;
}

/**
 * Drops into the full-page conversation when the landing composer sends. The
 * composer submits through the runtime directly, so there is no submit handler
 * to navigate from — the turn starting is the signal. `/chat/new` then syncs
 * to `/chat/:id` once the server mints the id.
 */
function NavigateOnFirstMessage(): null {
  const navigate = useNavigate();
  const routes = useRoutes();
  // A run starting is the signal. Watching the message count instead would
  // also fire when an existing conversation hydrates into the shared runtime
  // (history loading grows the count), bouncing the user into a chat they
  // never opened; `isRunning` only goes true for an actual stream.
  const isRunning = useAuiState(({ thread }) => thread.isRunning);
  const wasRunningRef = useRef(isRunning);
  useEffect(() => {
    const justStarted = isRunning && !wasRunningRef.current;
    wasRunningRef.current = isRunning;
    if (justStarted) void navigate(routes.chat.conversation.href("new"));
  }, [isRunning, navigate, routes]);
  return null;
}

/**
 * Command palette rendered under the composer while the user is typing a slash
 * query. Selection (keyboard or hover) is owned by the parent so Enter and
 * clicks resolve to the same highlighted row. Items use onMouseDown-prevent so
 * clicking one doesn't blur the input (which would close the menu first).
 */
function SlashCommandMenu({
  commands,
  activeIndex,
  onHover,
  onSelect,
}: {
  commands: InsightsSuggestion[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (command: InsightsSuggestion) => void;
}): ReactElement {
  const activeRef = useRef<HTMLButtonElement | null>(null);

  // Keep the keyboard-highlighted row visible. "nearest" is a no-op when the
  // row is already on-screen, so hovering visible rows never triggers a scroll.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <div
      id="ask-slash-menu"
      role="listbox"
      aria-label="Suggested prompts"
      className="border-border bg-card absolute inset-x-0 top-full z-20 mt-2 max-h-80 overflow-y-auto border p-1 shadow-lg"
    >
      {commands.map((command, index) => {
        const Icon = INSIGHTS_SUGGESTION_ICONS[command.icon ?? "sparkles"];
        const active = index === activeIndex;
        return (
          <button
            key={command.title}
            id={`ask-slash-${index}`}
            ref={active ? activeRef : undefined}
            type="button"
            role="option"
            aria-selected={active}
            // Prevent the input from blurring (which closes the menu) before
            // the click selects.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHover(index)}
            onClick={() => onSelect(command)}
            className={cn(
              // `bg-accent`, not `bg-muted` — on the home page the menu sits
              // inside a muted card, where a muted highlight is invisible.
              "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
              active ? "bg-accent" : "hover:bg-accent/60",
            )}
          >
            <Icon className="text-muted-foreground size-4 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span className="text-foreground truncate text-sm font-medium">
                {command.title}
              </span>
              <span className="text-muted-foreground truncate text-xs">
                {command.label}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Collapsed Recents shows just the latest few as a flat list; "Show all"
// expands to every conversation grouped by time period.
const RECENTS_COLLAPSED_COUNT = 3;

// Ordered time buckets. A null label renders no header (today's chats sit
// directly under the "Recents" heading, matching the flat collapsed view).
const RECENT_PERIODS: {
  key: string;
  label: string | null;
  maxAgeDays: number;
}[] = [
  { key: "today", label: null, maxAgeDays: 0 },
  { key: "yesterday", label: "Yesterday", maxAgeDays: 1 },
  { key: "week", label: "Last week", maxAgeDays: 7 },
  { key: "month", label: "Last month", maxAgeDays: 30 },
  { key: "earlier", label: "Earlier", maxAgeDays: Infinity },
];

type RecentEntry =
  | { type: "header"; key: string; label: string }
  | { type: "row"; key: string; chat: ChatOverview };

function startOfDay(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Compact relative age for a row, e.g. "Just now", "56m", "3h", "4d", "2w". */
function formatRelativeTime(date: Date): string {
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface ChatGroup {
  key: string;
  label: string | null;
  chats: ChatOverview[];
}

function groupChatsByPeriod(chats: ChatOverview[]): ChatGroup[] {
  const todayStart = startOfDay(new Date());
  const dayMs = 86_400_000;
  const groups: ChatGroup[] = RECENT_PERIODS.map((p) => ({
    key: p.key,
    label: p.label,
    chats: [],
  }));
  for (const chat of chats) {
    const ageDays = Math.floor(
      (todayStart - startOfDay(chat.lastMessageTimestamp)) / dayMs,
    );
    const index = RECENT_PERIODS.findIndex((p) => ageDays <= p.maxAgeDays);
    const group = groups[index === -1 ? groups.length - 1 : index];
    if (group) group.chats.push(chat);
  }
  return groups.filter((g) => g.chats.length > 0);
}

function buildRecentEntries(
  chats: ChatOverview[],
  showAll: boolean,
): RecentEntry[] {
  if (!showAll) {
    return chats
      .slice(0, RECENTS_COLLAPSED_COUNT)
      .map((chat) => ({ type: "row", key: `row:${chat.id}`, chat }));
  }
  const entries: RecentEntry[] = [];
  for (const group of groupChatsByPeriod(chats)) {
    if (group.label) {
      entries.push({
        type: "header",
        key: `header:${group.key}`,
        label: group.label,
      });
    }
    for (const chat of group.chats) {
      entries.push({ type: "row", key: `row:${chat.id}`, chat });
    }
  }
  return entries;
}

// Resolve the project's Project Assistant and list its conversations, filtered
// by pinned state. Shared by the pinned and recents sections so the
// assistant-resolution logic lives in one place.
function useProjectAssistantChats(pinned: Pinned): {
  chats: ChatOverview[];
  loading: boolean;
} {
  const { projectSlug } = useSlugs();
  // Reuse the dock's managed-assistant resolution to scope the list to this
  // project's Project Assistant conversations.
  const { assistantId, ready } = useServerAssistantTransport(
    projectSlug ?? "",
    true,
  );
  const enabled = Boolean(ready && assistantId);
  const { data, isPending } = useListChats(
    {
      assistantId: assistantId || undefined,
      // Mirror the Elements runtime request: hide onboarding/warmup threads
      // from the recents lists.
      excludeSourceKind: "setup",
      pinned,
      sortBy: SortBy.LastMessageTimestamp,
      sortOrder: SortOrder.Desc,
      limit: 50,
    },
    undefined,
    { enabled, throwOnError: false },
  );
  // A disabled query (no resolvable assistant) is never "loading" — report it
  // as settled-empty so the UI shows the empty state instead of spinning
  // forever.
  return { chats: data?.chats ?? [], loading: enabled && isPending };
}

// Pinned conversations, shown above recents. Hidden entirely when none exist.
function ChatHomePinned(): ReactElement | null {
  const { chats, loading } = useProjectAssistantChats(Pinned.True);
  if (loading || chats.length === 0) {
    return null;
  }
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-eyebrow px-3">Pinned</h2>
      <div className="flex flex-col">
        {chats.map((chat) => (
          <RecentRow key={chat.id} chat={chat} pinned />
        ))}
      </div>
    </section>
  );
}

function ChatHomeRecents(): ReactElement {
  // Exclude pinned chats — they live in the Pinned section above.
  const { chats, loading } = useProjectAssistantChats(Pinned.False);
  const [showAll, setShowAll] = useState(false);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-3">
        <h2 className="text-eyebrow">Recent Chats</h2>
        {chats.length > RECENTS_COLLAPSED_COUNT && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
          >
            {showAll ? "Show less" : "Show all"}
            {showAll ? (
              <Minus className="size-3.5" />
            ) : (
              <Plus className="size-3.5" />
            )}
          </button>
        )}
      </div>
      <RecentsBody chats={chats} loading={loading} showAll={showAll} />
    </section>
  );
}

// Recent threads shown by the compact (project home) variant. Deliberately a
// peek, not a list — "View more" hands off to the full `/chat` landing.
const COMPACT_RECENTS_COUNT = 3;

function ChatHomeCompactRecents(): ReactElement {
  const routes = useRoutes();
  // Pinned chats take the slots first; recents fill whatever is left.
  const { chats: pinnedChats, loading: pinnedLoading } =
    useProjectAssistantChats(Pinned.True);
  const { chats: recentChats, loading: recentLoading } =
    useProjectAssistantChats(Pinned.False);
  const loading = pinnedLoading || recentLoading;
  const chats = [
    ...pinnedChats.map((chat) => ({ chat, pinned: true })),
    ...recentChats.map((chat) => ({ chat, pinned: false })),
  ];
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-3">
        <h2 className="text-eyebrow">Recent Chats</h2>
        {chats.length > COMPACT_RECENTS_COUNT && (
          <Link
            to={routes.chat.href()}
            className="text-muted-foreground hover:text-foreground text-sm transition-colors"
          >
            View more
          </Link>
        )}
      </div>
      {loading ? (
        <p className="text-muted-foreground px-3 text-sm">
          Loading conversations…
        </p>
      ) : chats.length === 0 ? (
        <p className="text-muted-foreground px-3 text-sm">
          Your recent conversations will appear here.
        </p>
      ) : (
        <div className="flex flex-col">
          {chats.slice(0, COMPACT_RECENTS_COUNT).map(({ chat, pinned }) => (
            <RecentRow key={chat.id} chat={chat} pinned={pinned} />
          ))}
        </div>
      )}
    </section>
  );
}

function RecentsBody({
  chats,
  loading,
  showAll,
}: {
  chats: ChatOverview[];
  loading: boolean;
  showAll: boolean;
}): ReactElement {
  if (loading) {
    return (
      <p className="text-muted-foreground px-3 text-sm">
        Loading conversations…
      </p>
    );
  }
  if (chats.length === 0) {
    return (
      <p className="text-muted-foreground px-3 text-sm">
        Your recent conversations will appear here.
      </p>
    );
  }
  const entries = buildRecentEntries(chats, showAll);
  return (
    <motion.div layout className="flex flex-col">
      <AnimatePresence initial={false}>
        {entries.map((entry) => (
          <motion.div
            key={entry.key}
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <RecentEntryView entry={entry} />
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

function RecentEntryView({ entry }: { entry: RecentEntry }): ReactElement {
  if (entry.type === "header") {
    return <h3 className="text-eyebrow px-3 pt-4 pb-1">{entry.label}</h3>;
  }
  return <RecentRow chat={entry.chat} pinned={false} />;
}

/**
 * Two-letter initials from a display name or email handle: first letter of
 * the first and last word for a multi-word name ("Adam Bull" -> "AB"), or
 * the first two characters of the email's local part / a single-word name
 * otherwise ("adam@..." -> "AD").
 */
function initialsOf(identifier: string): string {
  const handle = identifier.includes("@")
    ? (identifier.split("@")[0] ?? identifier)
    : identifier;
  const words = handle.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (
      words[0]!.charAt(0) + words[words.length - 1]!.charAt(0)
    ).toUpperCase();
  }
  return handle.trim().slice(0, 2).toUpperCase();
}

// Row icon: the chat creator's avatar when it resolves against the org
// member list, otherwise the default message icon (e.g. chats with no
// internal user attached, or before members have loaded). Chats started from
// the dashboard itself have no `userId` at capture time and stash the
// caller's email in `externalUserId` instead — fall back to an email match so
// those still resolve.
function RecentRowIcon({
  userId,
  externalUserId,
}: {
  userId?: string;
  externalUserId?: string;
}): ReactElement {
  const isDark = useIsDarkTheme();
  const { data: membersData } = useMembers();
  const member = resolveChatOwner(membersData?.members, {
    userId,
    externalUserId,
  });

  if (member) {
    const display = member.name || member.email;
    return (
      <Avatar className="size-9 shrink-0">
        {member.photoUrl ? (
          <AvatarImage src={member.photoUrl} alt={display} />
        ) : null}
        <AvatarFallback
          className="text-xs font-medium"
          style={getIdentityTint(display, isDark)}
        >
          {initialsOf(display)}
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <span className="border-border bg-card text-muted-foreground flex size-9 shrink-0 items-center justify-center border">
      <MessageCircle className="size-4" />
    </span>
  );
}

function RecentRow({
  chat,
  pinned,
}: {
  chat: ChatOverview;
  pinned: boolean;
}): ReactElement {
  const routes = useRoutes();
  // `group/row` lets the pin affordance reveal on row hover. The row is a
  // container (not a Link) so the pin button isn't nested inside an anchor; the
  // Link covers the icon + title, and the pin button is a sibling action.
  return (
    <div className="group/row hover:border-foreground flex items-center gap-3 border border-transparent px-3 py-1.5 transition-colors">
      <Link
        to={routes.chat.conversation.href(chat.id)}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <RecentRowIcon
          userId={chat.userId}
          externalUserId={chat.externalUserId}
        />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">
          {chat.title || "New chat"}
        </span>
      </Link>
      <PinButton chatId={chat.id} pinned={pinned} />
      <span className="text-muted-foreground shrink-0 text-xs">
        {formatRelativeTime(chat.lastMessageTimestamp)}
      </span>
    </div>
  );
}

// Pin/unpin toggle for a chat row. An unpinned chat reveals an outline pin on
// row hover; a pinned chat shows a persistent filled pin. preventDefault +
// stopPropagation keep a pin click from triggering the row's navigation Link.
function PinButton({
  chatId,
  pinned,
}: {
  chatId: string;
  pinned: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const setPinned = useChatSetPinnedMutation();

  return (
    <button
      type="button"
      disabled={setPinned.isPending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setPinned.mutate(
          {
            request: { setPinnedRequestBody: { id: chatId, pinned: !pinned } },
          },
          { onSettled: () => void invalidateAllListChats(queryClient) },
        );
      }}
      aria-label={pinned ? "Unpin chat" : "Pin chat"}
      title={pinned ? "Unpin chat" : "Pin chat"}
      className={cn(
        "text-muted-foreground hover:text-foreground hover:bg-muted shrink-0 p-1 transition-opacity",
        pinned
          ? "text-foreground"
          : // Visible by default (touch has no hover to reveal it); only fade-until-hover
            // on devices that actually support hover.
            "focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/row:opacity-100",
      )}
    >
      <Pin className={cn("size-3.5", pinned && "fill-current")} aria-hidden />
    </button>
  );
}

/**
 * Starter prompt chips. Clicking one hands the chip element to `useChatLaunch`,
 * which flies it to the centre of the screen and then morphs it into the user
 * bubble of the conversation it just started.
 */
// Chips shown by the compact (project home) variant — two rows in the
// half-width column it sits in.
const COMPACT_SUGGESTION_COUNT = 4;

function ChatHomeSuggestions({
  compact = false,
}: {
  compact?: boolean;
}): ReactElement {
  const launchChat = useChatLaunch();
  // The full landing indents chips to line up with the recents rows; the
  // compact card has no rows, so chips align flush with the composer.
  const inset = compact ? "" : "px-3";
  // Compact shows a trimmed set so the chips stay within two rows at the
  // widths the project home page renders at.
  const suggestions = compact
    ? CHAT_LANDING_SUGGESTIONS.slice(0, COMPACT_SUGGESTION_COUNT)
    : CHAT_LANDING_SUGGESTIONS;
  return (
    <section className="flex flex-col gap-3">
      <h2 className={cn("text-eyebrow", inset)}>Suggestions</h2>
      <div className={cn("flex flex-wrap gap-x-2 gap-y-2.5", inset)}>
        {suggestions.map((suggestion) => {
          const SuggestionIcon =
            INSIGHTS_SUGGESTION_ICONS[suggestion.icon ?? "sparkles"];
          return (
            <button
              key={suggestion.title}
              type="button"
              onClick={(event) => launchChat(suggestion, event.currentTarget)}
              className="border-border bg-card text-foreground hover:border-foreground flex items-center gap-2 border px-3 py-2 text-sm transition-colors"
            >
              <SuggestionIcon className="size-4 shrink-0" />
              {suggestion.title}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * `/chat/:chatId` — a single conversation. `:chatId` is either a server chat
 * id (opened by <SavedConversation> on the shared runtime) or the literal
 * `new` for the active/fresh thread (a seeded prompt is already streaming, or
 * an empty composer for a brand-new chat).
 */
export function ChatConversation(): ReactElement {
  const { chatId } = useParams();
  const routes = useRoutes();
  const navigate = useNavigate();
  const { assistantReady, assistantNeedsAdmin, newConversation } =
    useInsightsState();

  const startNewChat = () => {
    newConversation();
    void navigate(routes.chat.conversation.href("new"));
  };

  return (
    <div className="flex h-full flex-col">
      {/* h-(--header-height) + px-8: same row height and content inset as
          Page.Header, so this header's rule lines up with the sidebar's. */}
      <header className="border-border flex h-(--header-height) shrink-0 items-center gap-3 border-b px-8">
        <Link
          to={routes.chat.href()}
          aria-label="Back to chat"
          className={ICON_BUTTON_CLASS}
        >
          <ChevronLeft className="size-4" />
        </Link>
        {assistantReady ? (
          <ActiveChatTitle size="base" className="min-w-0 flex-1" />
        ) : (
          <div className="min-w-0 flex-1" />
        )}
        <button
          type="button"
          onClick={startNewChat}
          className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-sm"
        >
          <SquarePen className="size-4" />
          New chat
        </button>
      </header>
      <div className="min-h-0 flex-1">
        <ConversationBody
          chatId={chatId}
          ready={assistantReady}
          needsAdmin={assistantNeedsAdmin}
        />
      </div>
    </div>
  );
}

function ConversationBody({
  chatId,
  ready,
  needsAdmin,
}: {
  chatId: string | undefined;
  ready: boolean;
  needsAdmin: boolean;
}): ReactElement {
  // The shared runtime (mounted in InsightsProvider) is the ancestor here, so
  // gate on it rather than mounting a second provider — that's what lets an
  // in-flight turn started in the dock keep streaming after maximize.
  if (needsAdmin) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center px-6 text-center text-sm">
        Ask an admin to enable the Project Assistant for this project.
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }
  return <ConversationSurface chatId={chatId} />;
}

function ConversationSurface({
  chatId,
}: {
  chatId: string | undefined;
}): ReactElement {
  const { pathname } = useLocation();
  const threadTitle = useAuiState((s) => s.threadListItem.title);
  // Recents would otherwise keep the section title ("Project Assistant") because
  // the conversation is keyed by an opaque chat id.
  useRecentLabelOverride(
    pathname,
    threadTitle?.trim() || (chatId === "new" ? FALLBACK_TITLE : undefined),
  );
  const activeRemoteId = useAuiState(
    ({ threadListItem }) => threadListItem.remoteId ?? null,
  );
  // Render the active thread directly when it's a fresh chat ("new") or its id
  // already matches the URL — including right after a new chat mints its id and
  // the URL syncs from /chat/new to /chat/:id. Keeping <ChatSurface> at the same
  // position across that swap means the conversation isn't remounted. Only a
  // URL pointing at a DIFFERENT thread routes through <SavedConversation>.
  if (!chatId || chatId === "new" || chatId === activeRemoteId) {
    return (
      <>
        {chatId === "new" && <NewChatUrlSync activeRemoteId={activeRemoteId} />}
        <ChatSurface />
      </>
    );
  }
  return <SavedConversation chatId={chatId} />;
}

/**
 * When a brand-new chat (`/chat/new`) mints its server id, replace the URL with
 * `/chat/:id` so it's shareable and survives a reload. The shared runtime is
 * keyed independently of the route, so this is a pure address-bar update — no
 * remount.
 */
function NewChatUrlSync({
  activeRemoteId,
}: {
  activeRemoteId: string | null;
}): null {
  const navigate = useNavigate();
  const routes = useRoutes();
  useEffect(() => {
    if (!activeRemoteId) return;
    void navigate(routes.chat.conversation.href(activeRemoteId), {
      replace: true,
    });
  }, [activeRemoteId, navigate, routes]);
  return null;
}

// The provider renders its own `gram-elements h-full` wrapper (a plain block,
// not a flex column), so the chat surface fills it with `h-full`; using
// `flex-1` here would be inert and let the composer overflow off-screen.
function ChatSurface(): ReactElement {
  // `gram-chat-fullpage` lets the shared Elements customCss give the composer a
  // roomier height on the full page (via :host-context) without affecting the
  // compact docked panel — see CHAT_FULLPAGE_COMPOSER_CSS in insights-dock.
  return (
    <div
      data-radius="sharp"
      className="gram-chat-fullpage h-full overflow-hidden"
    >
      <Chat />
    </div>
  );
}

/**
 * Opens a saved conversation by id and holds the chat surface back until it is
 * the active thread. The Elements provider's own `history.initialThreadId`
 * switch fires on a fixed 100ms timer with a one-shot guard that races the
 * `chat.list` fetch, so on a cold load it silently no-ops. Switching once the
 * list has loaded (`threads.isLoading === false`) is reliable, and gating on
 * the active thread's remote id stops the empty/welcome thread flashing before
 * the conversation binds.
 */
function SavedConversation({ chatId }: { chatId: string }): ReactElement {
  const aui = useAui();
  const isListLoading = useAuiState(({ threads }) => threads.isLoading);
  const activeRemoteId = useAuiState(
    ({ threadListItem }) => threadListItem.remoteId ?? null,
  );
  // Latch per chatId, not a one-shot boolean: navigating straight from one
  // saved conversation to another (same route, different :chatId) must switch
  // again, otherwise the view stays stuck on the previous thread's loader.
  const switchedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (isListLoading || switchedForRef.current === chatId) return;
    switchedForRef.current = chatId;
    aui.threads().switchToThread(chatId);
  }, [aui, chatId, isListLoading]);

  if (activeRemoteId !== chatId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }
  return <ChatSurface />;
}
